import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, type SyntheticEvent } from 'react';
import { isAppError } from '@/shared/api/errors';
import { useChangePassword, useSession, useSignOut } from '@/shared/session/useSession';
import { BrandMark } from '../layout/BrandMark';

const MIN_CHARS = 10;

function messageFor(error: unknown): string {
  if (isAppError(error) && (error.status === 422 || error.status === 429)) return error.message;
  return 'Could not change your password. Check your connection and try again.';
}

/** First sign-in with an admin's temporary password, or after a reset: the person sets their own. */
export function ChangePasswordPage() {
  const session = useSession();
  const change = useChangePassword();
  const signOut = useSignOut();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const tooShort = next.length > 0 && next.length < MIN_CHARS;
  const mismatch = repeat.length > 0 && repeat !== next;

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    change.mutate({ current, next });
  };

  return (
    <Box component="main" sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', px: 2, bgcolor: 'background.default' }}>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 } }}>
        <Stack component="form" onSubmit={submit} noValidate sx={{ gap: 2.5 }}>
          <BrandMark />
          <Stack sx={{ gap: 0.75 }}>
            <Typography variant="h2" component="h1">Set your password</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {session.data?.name ? `${session.data.name}, your` : 'Your'} account has a temporary password from an admin. Choose your own to continue.
            </Typography>
          </Stack>
          {change.isError ? <Alert severity="error">{messageFor(change.error)}</Alert> : null}
          <TextField label="Temporary password" type="password" autoComplete="current-password" value={current} onChange={(e) => { setCurrent(e.target.value); }} required />
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => { setNext(e.target.value); }}
            error={tooShort}
            helperText={`At least ${MIN_CHARS} characters.`}
            required
          />
          <TextField
            label="Repeat new password"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => { setRepeat(e.target.value); }}
            error={mismatch}
            helperText={mismatch ? 'The passwords do not match.' : ' '}
            required
          />
          <Button type="submit" variant="contained" size="large" loading={change.isPending} disabled={!current || next.length < MIN_CHARS || next !== repeat}>
            Save and continue
          </Button>
          <Button variant="text" onClick={() => { signOut.mutate(); }}>Sign out</Button>
        </Stack>
      </Paper>
    </Box>
  );
}
